// Maps layout-template ids (the same slugs Designer.LayoutTemplate stores) to
// the React components that render them. Unknown ids fall back to "standard"
// — same behaviour the server applies, but defensive in case the wire shape
// gets out of sync with what this bundle ships.
//
// Each layout can optionally supply its own *series picker* — the screen the
// user sees when the Designer has 2+ series and none is selected yet. Layouts
// that don't supply one fall back to the generic <SeriesPicker>.
//
// Adding a new layout: drop a component in this folder taking LayoutProps,
// register it here. The server-side DesignerLayouts entry's `Name` MUST match
// the key used here.

import type { ComponentType } from "react";
import { SeriesPicker } from "../components/SeriesPicker";
import { SingleMenuSeriesPicker } from "../components/SingleMenuSeriesPicker";
import { StandardLayout } from "./StandardLayout";
import { SingleMenuLayout } from "./SingleMenuLayout";
import { SINGLE_MENU_DEFAULT_MAPPING } from "./SingleMenuLayout.defaults";
import { SINGLE_MENU_MANIFEST } from "./SingleMenuLayout.manifest";
import { STANDARD_LAYOUT_MANIFEST } from "./StandardLayout.manifest";
import type { LayoutManifest } from "./manifest";
import type { LayoutProps } from "./types";
import type { DesignerSeriesDto } from "../types";
import type { SlotMapping } from "../blocks/types";

export interface SeriesPickerProps {
  series: DesignerSeriesDto[];
  onPick: (s: DesignerSeriesDto) => void;
  designerName?: string;
  brandLogoUrl?: string | null;
  /** Block host context, for pickers that compose their chrome from <Slot>.
   *  Optional so simpler pickers (the generic SeriesPicker) can ignore it. */
  blockHost?: import("../blocks/types").BlockHostContext;
  slotMappingOverride?: import("../blocks/types").SlotMapping | null;
}

interface LayoutEntry {
  Component: ComponentType<LayoutProps>;
  /** Picker component for the 2+ series initial screen. Optional. */
  SeriesPicker?: ComponentType<SeriesPickerProps>;
  /** Slot declarations for the authoring UI. Empty array = layout doesn't
   *  expose any slots (the standard layout today). */
  manifest: LayoutManifest;
  /** Default slot mapping for this layout — what renders when the Designer
   *  doesn't override. The slot-mapping editor reads this to show "what you'd
   *  get without an override" alongside the operator's edits. */
  defaultMapping: SlotMapping;
}

const REGISTRY: Record<string, LayoutEntry> = {
  standard: {
    Component: StandardLayout,
    // Standard layout uses the plain card-grid SeriesPicker — already its default.
    manifest: STANDARD_LAYOUT_MANIFEST,
    defaultMapping: {},
  },
  "single-menu": {
    Component: SingleMenuLayout,
    SeriesPicker: SingleMenuSeriesPicker,
    manifest: SINGLE_MENU_MANIFEST,
    defaultMapping: SINGLE_MENU_DEFAULT_MAPPING,
  },
};

// Legacy slug → current slug. The server normalises this on the wire (see
// DesignerLayouts.GetByName), but we resolve it here too so a stale id arriving
// any other way (e.g. a ?layoutTemplate=editorial-glass dev override) still maps
// to the current layout.
const LAYOUT_ALIASES: Record<string, string> = {
  "editorial-glass": "single-menu",
};

export const DEFAULT_LAYOUT_ID = "standard";

/** Resolve a (possibly legacy) layout id to its canonical slug. */
export function canonicalLayoutId(id: string | null | undefined): string {
  if (!id) return DEFAULT_LAYOUT_ID;
  const resolved = LAYOUT_ALIASES[id] ?? id;
  return REGISTRY[resolved] ? resolved : DEFAULT_LAYOUT_ID;
}

function entry(id: string | null | undefined): LayoutEntry {
  return REGISTRY[canonicalLayoutId(id)];
}

export function pickLayout(id: string | null | undefined): ComponentType<LayoutProps> {
  return entry(id).Component;
}

export function pickSeriesPicker(id: string | null | undefined): ComponentType<SeriesPickerProps> {
  return entry(id).SeriesPicker ?? SeriesPicker;
}

export function getLayoutManifest(id: string | null | undefined): LayoutManifest {
  return entry(id).manifest;
}

export function getLayoutDefaultMapping(id: string | null | undefined): SlotMapping {
  return entry(id).defaultMapping;
}

/** All registered layouts' manifests + default mappings. The build target
 *  reads this to emit a manifest JSON the server consumes. */
export function listLayoutsForAuthoring(): Array<{
  id: string;
  manifest: LayoutManifest;
  defaultMapping: SlotMapping;
}> {
  return Object.entries(REGISTRY).map(([id, entry]) => ({
    id,
    manifest: entry.manifest,
    defaultMapping: entry.defaultMapping,
  }));
}
